import React, { useEffect, useState } from 'react';
import { api } from '../api/client';
import { PageHeader, Card, Badge, EmptyState, formatDateTime } from '../components/UI';

export default function Dashboard({ onNav }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState('');

  useEffect(() => {
    api.dashboard()
      .then(setData)
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;
  if (err)     return <PageHeader title="Error" subtitle={err} />;

  const { counts, subscriptionStatus, expiringPatients, topClasses, recentActivity } = data;

  return (
    <>
      <PageHeader
        eyebrow="Resumen"
        title="Dashboard"
        subtitle="Vista general del sistema"
      />

      {/* Métricas principales */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 12,
        marginBottom: 32,
      }}>
        <Metric label="Pacientes activos" value={counts.active_patients} />
        <Metric label="Clases publicadas" value={counts.published_classes} />
        <Metric label="Audios totales" value={counts.total_audios} />
        <Metric label="Audiciones (30d)" value={counts.plays_last_30d} />
      </div>

      {/* Estado de suscripciones */}
      <h2 style={sectionTitle}>Estado de suscripciones</h2>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 10,
        marginBottom: 32,
      }}>
        <SubStatusCard label="Activas" value={subscriptionStatus.active_count || 0} tone="success" />
        <SubStatusCard label="Por vencer" value={subscriptionStatus.expiring_count || 0} tone="warning" />
        <SubStatusCard label="Vencidas" value={subscriptionStatus.expired_count || 0} tone="danger" />
        <SubStatusCard label="Sin suscripción" value={subscriptionStatus.no_sub_count || 0} tone="neutral" />
      </div>

      {/* Avisos urgentes */}
      {expiringPatients.length > 0 && (
        <>
          <h2 style={sectionTitle}>Requieren atención</h2>
          <Card padding={0} style={{ marginBottom: 32 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={tableHeadRow}>
                  <th style={tableHeadCell}>Paciente</th>
                  <th style={tableHeadCell}>Email</th>
                  <th style={tableHeadCell}>Vence</th>
                  <th style={tableHeadCell}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {expiringPatients.map(p => (
                  <tr key={p.id} style={tableRow}
                      onClick={() => onNav('patients', { id: p.id })}>
                    <td style={tableCell}>{p.name}</td>
                    <td style={{ ...tableCell, color: 'var(--ink-soft)', fontSize: 12 }}>{p.email}</td>
                    <td style={{ ...tableCell, fontSize: 12 }}>
                      {new Date(p.end_date).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })}
                    </td>
                    <td style={tableCell}>
                      {p.state === 'expired' ? (
                        <Badge tone="danger">Vencida {Math.abs(p.days_left)}d</Badge>
                      ) : p.days_left === 0 ? (
                        <Badge tone="warning">Vence hoy</Badge>
                      ) : (
                        <Badge tone="warning">{p.days_left}d restantes</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}

      {/* Top clases y actividad reciente */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
        gap: 20,
        marginBottom: 32,
      }}>
        <div>
          <h2 style={sectionTitle}>Clases más escuchadas</h2>
          <Card padding={0}>
            {topClasses.length === 0 ? (
              <EmptyState>No hay datos aún</EmptyState>
            ) : (
              <div>
                {topClasses.slice(0, 8).map((c, i) => (
                  <div key={c.id} style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '12px 16px',
                    borderBottom: i < topClasses.length - 1 ? '1px solid var(--border)' : 'none',
                  }}>
                    <div style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      fontSize: 22, color: 'var(--stone)', minWidth: 24,
                    }}>{i + 1}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--earth)' }}>
                        {c.code}
                      </div>
                      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 15 }}>
                        {c.title}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 14, fontWeight: 400 }}>{c.play_count}</div>
                      <div style={{ fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--stone)' }}>
                        {c.unique_listeners} pacientes
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        <div>
          <h2 style={sectionTitle}>Actividad reciente</h2>
          <Card padding={0}>
            {recentActivity.length === 0 ? (
              <EmptyState>Sin actividad</EmptyState>
            ) : (
              <div style={{ maxHeight: 460, overflowY: 'auto' }}>
                {recentActivity.map((a, i) => (
                  <div key={i} style={{
                    padding: '10px 16px',
                    borderBottom: i < recentActivity.length - 1 ? '1px solid var(--border)' : 'none',
                    display: 'flex', alignItems: 'center', gap: 10,
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13 }}>{a.patient_name}</div>
                      <div style={{ fontSize: 11, color: 'var(--ink-soft)' }}>
                        {a.class_code} · {a.class_title}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--stone)', textAlign: 'right' }}>
                      {formatDateTime(a.played_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}

const sectionTitle = {
  fontSize: 11, fontWeight: 300, letterSpacing: '0.22em',
  textTransform: 'uppercase', color: 'var(--earth)',
  marginBottom: 14, marginTop: 4,
};

const tableHeadRow = { borderBottom: '1px solid var(--border)' };
const tableHeadCell = {
  textAlign: 'left', padding: '12px 16px',
  fontSize: 9, fontWeight: 300, letterSpacing: '0.18em',
  textTransform: 'uppercase', color: 'var(--earth)',
};
const tableRow = {
  borderBottom: '1px solid var(--border)',
  cursor: 'pointer',
  transition: 'background 0.1s',
};
const tableCell = { padding: '12px 16px', fontSize: 13 };

function Metric({ label, value }) {
  return (
    <Card>
      <div style={{ fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--earth)', marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 36, fontWeight: 300, lineHeight: 1 }}>
        {value || 0}
      </div>
    </Card>
  );
}

function SubStatusCard({ label, value, tone }) {
  const colors = {
    success: { color: 'var(--success)', bg: 'var(--success-bg)' },
    warning: { color: 'var(--warn)', bg: 'var(--warn-bg)' },
    danger:  { color: 'var(--danger)', bg: 'var(--danger-bg)' },
    neutral: { color: 'var(--ink-soft)', bg: 'var(--sand-2)' },
  };
  const c = colors[tone];
  return (
    <div style={{
      padding: 16,
      background: c.bg,
      borderRadius: 6,
      border: '1px solid transparent',
    }}>
      <div style={{ fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: c.color, marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 300, lineHeight: 1, color: c.color }}>
        {value}
      </div>
    </div>
  );
}

function Loading() {
  return (
    <div style={{ padding: 60, textAlign: 'center', color: 'var(--stone)' }}>
      Cargando…
    </div>
  );
}
